// The waypoint list inside the trip's edit controls: one row per point of
// the route, with rename / reorder / insert / delete / locate. A third editing
// entry point alongside the map's add-waypoint mode and marker modal -- all
// three mutate the same route object through onRouteChange, so dragging a
// marker updates these rows and vice versa.
//
// Deliberately maplibre-free (only TripRoute.mjs + react-bootstrap), so it
// renders immediately while the map chunk is still loading.
//
// Row rules, which follow the route contract in TripRoute.mjs:
// - From/To are ANCHORS: renameable, but not reorderable or deletable.
//   `routeFromTrip` re-sorts to [from, ...vias, to] on load, so a via that
//   crossed an anchor would silently revert after a save, and
//   `removeWaypoint` ignores anchors outright.
// - Vias reorder only among themselves (the arrows disable at the ends).
// - A via can be a ZERO-DAY STOPOVER: somewhere you stop without staying the
//   night -- a picnic stop, a fuel stop. It is offered on vias alone because
//   Start and End are where the leg starts and ends its day, which is the
//   opposite claim (see TripRoute.mjs's stopover section for why it is a flag
//   on a waypoint and not a leg of its own). The details behind the switch
//   only exist while it is on, so switching it off drops them -- the model
//   does that, not this component, so the row and the next page load agree.
// - "Insert below" adds a point at that leg's midpoint, auto-named with its
//   coordinates; it needs a leg to interpolate along, so it's hidden unless
//   this row and something below it are both on the map.
// - `collapsibleVias` puts the via rows behind a disclosure toggle, shut to
//   begin with. It is OFF by default, which is what keeps every existing
//   caller and test unchanged, and only the logbook entry page turns it on: an
//   entry records a voyage that already happened, so its route arrived whole
//   (TripToLogbook, or one drawing) and the vias are a RECORD rather than the
//   work -- while Hours, the counters and Notes, which are what that page is
//   for, get pushed off the screen by a long list. A trip LEG is the opposite
//   case, so Trip.jsx deliberately doesn't pass it. Anchors never collapse:
//   Start and End are the trip's From/To editor.
//
// Under the name box a row shows where it is and how to (re)place it: the
// coordinates when it's placed, and the two ways onto the map -- "Lookup place"
// (the geocoder, offered only while the point is unplaced) and "Place on map"
// (hand the next map tap to this point, offered always, because a point in the
// wrong spot needs re-placing without a rename first). Once a row owns the next
// tap it says so, with a Cancel. All of it runs through the shared `locator` so
// the map and this list can't both claim a tap.
//
// Typing a NEW name unplaces a point whose position was placed under the old
// name -- on the first divergent keystroke, not on blur, so the autosave
// debounce can never persist the new name over the stale position. Typing the
// old name back within the same focus session restores the pin (an accidental
// keystroke shouldn't cost a placement). See `nameOwnsPosition` in
// TripRoute.mjs for why a point being named for the first time (unnamed or
// auto-named) keeps its pin.
//
// Rows are otherwise controlled straight off `route` -- no per-row useState --
// so a marker drag on the map can't desync the inputs. The one ref here holds
// the waypoint as it was when the box took focus, which is the only thing a
// controlled row can't tell you mid-edit.
//
// The Start and End rows ARE the trip's From/To editor: the detail page has
// no separate From/To inputs. A placed anchor is renamed through
// `onRouteChange` like any other waypoint, and the PAGE mirrors that name
// into the `from`/`to` column -- it does the same for the map's marker
// modal, so the rule lives in one place instead of once per editor.
// `onEndpointChange` is only for an anchor that isn't on the map yet, where
// there's no waypoint to rename and the column is all there is.
// Row labels stay unique per row ("Waypoint 2 name", "Start name") because
// the page tests query by exact label text.

import { useEffect, useId, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import {
  ANCHOR_COPY,
  ANCHOR_KINDS,
  MAX_STOP_NOTE_LENGTH,
  formatLatLon,
  insertViaAfter,
  isPlaced,
  isStopover,
  makeWaypoint,
  moveWaypointOrder,
  nameOwnsPosition,
  placeWaypoint,
  renameWaypoint,
  removeWaypoint,
  setStopDetails,
  setStopover,
  unplaceWaypoint,
  upsertAnchor,
} from '../../fleet-shared/TripRoute.mjs';
import PlaceSuggestInput from '../../components/PlaceSuggestInput.jsx';
import { recordCommittedRename, recordPickedPlace } from '../../utilities/places.mjs';

// The two anchor kinds, in route order, with the label a row refers to itself
// by and its empty-box prompt. Vias are "Waypoint <n>" by position among the
// vias. The copy is shared with the map card (ANCHOR_COPY) so the list and the
// map name the same two things the same way.
const ANCHORS = ANCHOR_KINDS.map((kind) => ({ kind, ...ANCHOR_COPY[kind] }));

const VIA_PLACEHOLDER = 'Name this waypoint';

// The label + name box every row shares, so a real waypoint row and an
// anchor placeholder can't drift apart.
const NameField = ({
  rowLabel,
  value,
  placeholder,
  selectOnFocus,
  onChange,
  onPick,
  onFocus,
  onBlur,
  children,
}) => {
  const nameId = useId();

  return (
    <>
      {/* Column width and label classes match EditForm's `wide` Field rows, so
          the trip's labels and these line up down the card. Change both. */}
      <div className="col-12 col-sm-3 col-lg-2">
        <label htmlFor={nameId} className="form-label mb-0">
          {rowLabel}
        </label>
      </div>

      <div className="col-12 col-sm">
        <PlaceSuggestInput
          id={nameId}
          size="sm"
          value={value}
          ariaLabel={`${rowLabel} name`}
          placeholder={placeholder}
          selectOnFocus={selectOnFocus}
          onChange={onChange}
          onPick={onPick}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {children}
      </div>
    </>
  );
};

// What sits under the name box: the ways to get this point onto the map.
// "Lookup place" is only for a point that isn't on one -- a placed point's
// name has already been answered, and a rename unplaces it again, which is
// what re-offers the button. "Place on map" is always there: it's the only
// way to correct a pin from the list, and the only way at all for a name no
// geocoder knows.
//
// While this row owns the next tap it says so instead, with a way out --
// arming is a deliberate click now, so an accidental one needs an exit that
// isn't "tap the map somewhere and undo it".
const PlacementControls = ({ rowLabel, target, placed, locator }) => {
  if (!locator) return null;

  if (locator.isPending(target)) {
    return (
      <div className="small mt-1 d-flex flex-wrap gap-2" data-testid="waypoint-pending">
        <span className="text-body-secondary">Tap the map to place it</span>
        <Button
          variant="link"
          size="sm"
          className="p-0 small text-decoration-none"
          data-testid="waypoint-cancel-place"
          aria-label={`Cancel placing ${rowLabel}`}
          onClick={() => locator.cancelPlacement(target)}
        >
          Cancel
        </Button>
      </div>
    );
  }

  const locating = locator.isLocating(target);
  return (
    <div className="small mt-1 d-flex flex-wrap gap-2">
      {!placed && (
        <Button
          variant="link"
          size="sm"
          className="p-0 small text-decoration-none"
          data-testid="waypoint-locate"
          aria-label={`Lookup place for ${rowLabel}`}
          disabled={locating}
          onClick={() => locator.locate([target])}
        >
          {locating ? 'Locating…' : 'Lookup place'}
        </Button>
      )}
      <Button
        variant="link"
        size="sm"
        className="p-0 small text-decoration-none"
        data-testid="waypoint-place"
        aria-label={`Place ${rowLabel} on map`}
        onClick={() => locator.armPlacement([target])}
      >
        Place on map
      </Button>
    </div>
  );
};

// The zero-day stopover switch and, once it is on, what the stop was: a note
// and the clock times either side of it.
//
// Vias only -- the caller decides that, the same way it decides which rows get
// the reorder arrows -- and the model refuses an anchor anyway.
//
// The boxes are controlled straight off the route like every other row here,
// so a stop edited from the map's marker dialog shows up in them and vice
// versa. That is also why the note is never trimmed on the way in
// (normalizeStopNote's rule): it would eat the space in "fuel, " as fast as it
// was typed.
//
// The times are a TIME OF DAY. A waypoint has no date of its own -- the leg
// carries that, and it moves every time the days chain cascades -- so
// <input type="time"> is the right control and a datetime one would be a bug.
//
// Labels are per-row ("Waypoint 2 stopover note") because several rows render
// these at once and the suites query by exact label text.
const StopoverControls = ({ waypoint, rowLabel, onToggle, onDetails }) => {
  const switchId = useId();
  const noteId = useId();
  const arriveId = useId();
  const departId = useId();
  const on = isStopover(waypoint);

  const time = (id, label, key) => (
    <div className="col-6 col-sm-auto">
      <label htmlFor={id} className="form-label mb-0 small text-body-secondary">
        {label}
      </label>
      <Form.Control
        id={id}
        type="time"
        size="sm"
        value={waypoint[key] || ''}
        aria-label={`${rowLabel} ${label.toLowerCase()} time`}
        onChange={(event) => onDetails(waypoint.id, { [key]: event.target.value })}
      />
    </div>
  );

  return (
    <div className="mt-1" data-testid="waypoint-stopover">
      <Form.Check
        type="switch"
        id={switchId}
        className="small"
        checked={on}
        label="Stopover"
        aria-label={`Mark ${rowLabel} as a stopover`}
        onChange={(event) => onToggle(waypoint.id, event.target.checked)}
      />

      {on && (
        <div className="row g-2 mt-1" data-testid="waypoint-stopover-details">
          {time(arriveId, 'Arrive', 'arriveAt')}
          {time(departId, 'Depart', 'departAt')}
          <div className="col-12 col-sm">
            <label htmlFor={noteId} className="form-label mb-0 small text-body-secondary">
              What happens here
            </label>
            <Form.Control
              id={noteId}
              size="sm"
              value={waypoint.stopNote || ''}
              maxLength={MAX_STOP_NOTE_LENGTH}
              placeholder="Fuel, lunch, crew change…"
              aria-label={`${rowLabel} stopover note`}
              onChange={(event) => onDetails(waypoint.id, { stopNote: event.target.value })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const WaypointRow = ({
  waypoint,
  rowLabel,
  placeholder,
  isVia,
  canMoveUp,
  canMoveDown,
  canInsertBelow,
  locator,
  onRename,
  onPickPlace,
  onNameFocus,
  onNameBlur,
  onMove,
  onInsert,
  onRemove,
  onStopover,
  onStopDetails,
}) => (
  <div className="row g-2 align-items-center py-2 border-bottom" data-testid="waypoint-row">
    <NameField
      rowLabel={rowLabel}
      value={waypoint.name || ''}
      placeholder={placeholder}
      selectOnFocus={Boolean(waypoint.autoName)}
      onChange={(name) => onRename(waypoint.id, name)}
      onPick={(place) => onPickPlace(waypoint, place)}
      onFocus={() => onNameFocus(waypoint)}
      onBlur={() => onNameBlur(waypoint)}
    >
      {/* An auto-named waypoint's name IS its position, so the readout would
          just repeat the box above it. The controls stay either way. */}
      {isPlaced(waypoint) && !waypoint.autoName && (
        <div className="small text-body-secondary mt-1" data-testid="waypoint-coords">
          {formatLatLon(waypoint.lat, waypoint.lon)}
        </div>
      )}
      <PlacementControls
        rowLabel={rowLabel}
        target={{ id: waypoint.id }}
        placed={isPlaced(waypoint)}
        locator={locator}
      />

      {/* Vias only: Start and End are where the leg starts and ends its day,
          so a stopover switch on one would be the opposite claim. */}
      {isVia && (
        <StopoverControls
          waypoint={waypoint}
          rowLabel={rowLabel}
          onToggle={onStopover}
          onDetails={onStopDetails}
        />
      )}
    </NameField>

    <div className="col-12 col-sm-auto d-flex gap-1 justify-content-end">
      {isVia && (
        <>
          <Button
            variant="outline-secondary"
            size="sm"
            aria-label={`Move ${rowLabel} up`}
            disabled={!canMoveUp}
            onClick={() => onMove(waypoint.id, -1)}
          >
            <i className="fa-solid fa-chevron-up" aria-hidden="true"></i>
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            aria-label={`Move ${rowLabel} down`}
            disabled={!canMoveDown}
            onClick={() => onMove(waypoint.id, 1)}
          >
            <i className="fa-solid fa-chevron-down" aria-hidden="true"></i>
          </Button>
        </>
      )}

      {canInsertBelow && (
        <Button
          variant="outline-secondary"
          size="sm"
          aria-label={`Insert waypoint after ${rowLabel}`}
          onClick={() => onInsert(waypoint.id)}
        >
          <i className="fa-solid fa-plus" aria-hidden="true"></i>
        </Button>
      )}

      {isVia && (
        <Button
          variant="outline-danger"
          size="sm"
          aria-label={`Delete ${rowLabel}`}
          onClick={() => onRemove(waypoint.id)}
        >
          <i className="fa-solid fa-trash" aria-hidden="true"></i>
        </Button>
      )}
    </div>
  </div>
);

// An anchor that isn't in the route at all: a name to geocode from, and
// nothing to reorder, insert beside, or show coordinates for. Looking it up --
// or tapping it onto the map -- creates the waypoint (there's no id to place
// yet, so the target is the kind).
const AnchorPlaceholderRow = ({ kind, rowLabel, value, placeholder, locator, onChange, onPick }) => (
  <div className="row g-2 align-items-center py-2 border-bottom" data-testid="waypoint-row">
    <NameField
      rowLabel={rowLabel}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      onPick={(place) => onPick(kind, place)}
    >
      <PlacementControls rowLabel={rowLabel} target={{ kind }} placed={false} locator={locator} />
    </NameField>
    <div className="col-12 col-sm-auto" />
  </div>
);

const TripWaypoints = ({
  route,
  endpoints,
  locator,
  onRouteChange,
  onEndpointChange,
  collapsibleVias = false,
}) => {
  const waypoints = route?.waypoints || [];
  const vias = waypoints.filter((wp) => wp.kind === 'via');
  // Named in the shut disclosure's label. Without it the one page that
  // collapses its vias -- the logbook entry -- hides the fact that the voyage
  // had a fuel stop behind a button that only says how many waypoints there
  // are, which is the thing about the route a reader is most likely to want.
  const stopovers = vias.filter(isStopover).length;

  // Shut to begin with wherever the toggle is offered at all. `collapsed` is
  // the one expression the rows, the label and aria-expanded all read, so
  // there's no second answer to "is the list open" -- and with no vias there
  // is nothing to collapse, so the toggle doesn't render and this is false.
  const [expanded, setExpanded] = useState(false);
  const viasId = useId();
  const collapsed = collapsibleVias && vias.length > 0 && !expanded;

  // A via created while the list is shut would otherwise land on the map as a
  // marker with no box to name it -- both the map's add-waypoint mode and the
  // row "+" do exactly that, and neither is reachable from a row you can see.
  // So growth opens the list. The ref starts at the mount-time count, which is
  // what stops opening a page from counting as growth, and it tracks the count
  // whether or not the toggle is offered -- but only a collapsible list writes
  // state, so the planning page pays no re-render for a feature it doesn't
  // have.
  const viaCount = useRef(vias.length);
  useEffect(() => {
    if (collapsibleVias && vias.length > viaCount.current) setExpanded(true);
    viaCount.current = vias.length;
  }, [collapsibleVias, vias.length]);

  // The waypoint as it was when its name box took focus. Each keystroke is
  // judged against this snapshot -- it carries the focus-time name, position,
  // and autoName, none of which the live (already-renamed) waypoint can still
  // answer for. Blur only ends the session; a later refocus snapshots the
  // now-unplaced point, so re-typing the old name in a NEW session correctly
  // does not restore the position.
  const focusedWaypoint = useRef(null);

  function handleNameFocus(waypoint) {
    focusedWaypoint.current = waypoint;
  }

  // Blur ends the edit session -- and is the commit point for sharing a
  // first naming to the place database: a placed point whose pin SURVIVED
  // the session's typing (handleRename never unplaces a non-owning point --
  // an unnamed tap or autoName coordinate string) is the user asserting
  // name -> position. recordCommittedRename no-ops for every other case,
  // including the name-owning points handleRename already unplaced.
  //
  // Judge against the focus-time snapshot but record from the LIVE waypoint:
  // dragging the marker while this box is focused moves the live point, and
  // the shared row should get where the pin actually ended up, not the
  // pre-drag snapshot.
  function handleNameBlur(waypoint) {
    const focused = focusedWaypoint.current;
    focusedWaypoint.current = null;
    if (!focused || focused.id !== waypoint.id) return;
    if ((focused.name || '') === (waypoint.name || '')) return;
    recordCommittedRename(focused, waypoint.name, waypoint);
  }

  // A rename lands per keystroke. If the focus-time point's position belonged
  // to its name (nameOwnsPosition), a divergent name unplaces it right away --
  // strictly before the debounced autosave can run, so a mid-edit save never
  // carries the new name with the stale position -- and typing back the exact
  // focus-time name restores the stashed position.
  function handleRename(id, name) {
    let next = renameWaypoint(route, id, name);
    const focused = focusedWaypoint.current;
    if (focused && focused.id === id && nameOwnsPosition(focused)) {
      if ((name || '') === (focused.name || '')) {
        next = placeWaypoint(next, id, {
          lat: focused.lat,
          lon: focused.lon,
          src: focused.src,
          geocodedAt: focused.geocodedAt,
          // The gazetteer binding is stashed and restored with the position:
          // an accidental keystroke shouldn't cost the link any more than it
          // costs the pin, and a placed-but-unbound point would silently stop
          // following the place database.
          placeId: focused.placeId,
        });
      } else {
        next = unplaceWaypoint(next, id);
      }
    }
    onRouteChange(next);
  }

  // A picked suggestion is a rename AND a placement in one edit -- rename
  // first, so placeWaypoint (which refreshes auto-generated names from the
  // coordinates) keeps the picked name. The pick is also the commit:
  // re-snapshot the focus ref so the rest of the session judges against the
  // post-pick waypoint (an untouched blur is a no-op; a divergent keystroke
  // afterwards unplaces via handleRename, and retyping the picked name
  // restores the picked position).
  //
  // A pick off the GEOCODER tier (source 'photon') also teaches the shared
  // database, the same way handleNameBlur delegates a committed rename to
  // recordCommittedRename. A pick off the stored tier writes nothing: the row
  // is already there, which is why it was offered. Only a pick writes --
  // merely showing a suggestion does not, or the gazetteer would fill up with
  // candidates nobody chose (see recordPickedPlace).
  //
  // A Photon pick that lands next to a place that already exists asks first.
  // mergeSuggestions dedupes the two tiers by NAME, so a candidate spelled even
  // slightly differently from a stored row is offered alongside it and picking
  // it used to mint a duplicate silently -- which is how prod got two rows for
  // Victoria's Causeway Marina. confirmNewPlace runs `onCreate` synchronously
  // when there is nothing nearby, so the ordinary pick is unchanged.
  //
  // The route edit above this has ALREADY happened, and deliberately: the pick
  // is the commit, and PlaceSuggestInput closes its list on mouseDown
  // precisely so the input doesn't blur first. Deferring the edit behind a
  // dialog would let handleNameBlur run against the pre-pick snapshot and
  // unplace the point. Only the gazetteer write waits for an answer.
  function recordIfNew({ name, lat, lon, source }, target) {
    if (source !== 'photon') return;
    // No locator means no loaded index to ask against and nowhere to render
    // the dialog -- the same fail-open the index-less case takes inside
    // confirmNewPlace. PlacementControls already treats a missing locator as
    // "this surface has no placement affordances at all".
    if (!locator?.confirmNewPlace) {
      recordPickedPlace({ name, lat, lon });
      return;
    }
    locator.confirmNewPlace({
      point: { lat, lon },
      name,
      onCreate: () => recordPickedPlace({ name, lat, lon }),
      onUse: (existing) => locator.bindTargetToPlace(target, existing, name),
    });
  }

  function handlePickPlace(waypoint, place) {
    const { name, lat, lon } = place;
    const next = placeWaypoint(renameWaypoint(route, waypoint.id, name), waypoint.id, {
      lat,
      lon,
      src: 'geocode',
      geocodedAt: new Date().toISOString(),
      // Bind to the row when the pick came off the stored tier. A Photon pick
      // has no id yet -- recordIfNew creates the row fire-and-forget -- so it
      // binds by name on the next resolve instead.
      placeId: place.id || undefined,
    });
    onRouteChange(next);
    focusedWaypoint.current = next.waypoints.find((wp) => wp.id === waypoint.id) || null;
    recordIfNew(place, { id: waypoint.id });
  }

  // A pick on an anchor that isn't in the route yet creates it placed, the
  // same shape useRouteLocator's applyPosition builds for a {kind} target.
  // The page mirrors the anchor's name into the from/to column on route
  // change, so onEndpointChange isn't involved.
  function handlePickAnchor(kind, place) {
    const { name, lat, lon } = place;
    onRouteChange(
      upsertAnchor(
        route,
        makeWaypoint({
          kind,
          name,
          lat,
          lon,
          src: 'geocode',
          geocodedAt: new Date().toISOString(),
          placeId: place.id || undefined,
        })
      )
    );
    recordIfNew(place, { kind });
  }

  function renderWaypoint(waypoint, rowLabel, placeholder) {
    const isVia = waypoint.kind === 'via';
    const viaIndex = vias.findIndex((wp) => wp.id === waypoint.id);
    const index = waypoints.indexOf(waypoint);
    return (
      <WaypointRow
        key={waypoint.id}
        waypoint={waypoint}
        rowLabel={rowLabel}
        placeholder={placeholder}
        isVia={isVia}
        canMoveUp={isVia && viaIndex > 0}
        canMoveDown={isVia && viaIndex < vias.length - 1}
        // The midpoint needs a leg: this point and something below it both on
        // the map. (An anchor that's still a placeholder isn't in `waypoints`,
        // so a route missing its End correctly offers no insert on the last
        // via.)
        canInsertBelow={isPlaced(waypoint) && waypoints.slice(index + 1).some(isPlaced)}
        locator={locator}
        onRename={handleRename}
        onPickPlace={handlePickPlace}
        onNameFocus={handleNameFocus}
        onNameBlur={handleNameBlur}
        onMove={(id, delta) => onRouteChange(moveWaypointOrder(route, id, delta))}
        onInsert={(id) => onRouteChange(insertViaAfter(route, id))}
        onRemove={(id) => onRouteChange(removeWaypoint(route, id))}
        onStopover={(id, on) => onRouteChange(setStopover(route, id, on))}
        onStopDetails={(id, details) => onRouteChange(setStopDetails(route, id, details))}
      />
    );
  }

  // Start and End always render, so the trip's endpoints are nameable before
  // -- and while -- they're being placed on the map.
  function renderAnchor({ kind, label, placeholder }) {
    const waypoint = waypoints.find((wp) => wp.kind === kind);
    if (waypoint) return renderWaypoint(waypoint, label, placeholder);
    return (
      <AnchorPlaceholderRow
        key={kind}
        kind={kind}
        rowLabel={label}
        value={endpoints?.[kind] || ''}
        placeholder={placeholder}
        locator={locator}
        onChange={(name) => onEndpointChange?.(kind, name)}
        onPick={handlePickAnchor}
      />
    );
  }

  return (
    <div className="container mb-3">
      {/* Plain h6: same family/size/colour as the field labels above, just a
          heavier weight so it still reads as a section break. */}
      <h6 className="mb-2">Waypoints</h6>

      {renderAnchor(ANCHORS[0])}

      {/* A disclosure, not a Bootstrap Accordion: its card chrome and body
          padding would indent the via rows out of line with the label grid
          NameField shares with EditForm's `wide` rows, and these rows are part
          of the form rather than a widget sitting in it. Deliberately NOT
          data-testid="waypoint-row" -- the suite counts those and destructures
          them positionally. Collapsing while a via is armed for placement
          strands nothing: TripRouteMap renders locator.pendingHint with its own
          Cancel over the map. */}
      {collapsibleVias && vias.length > 0 && (
        <div className="row g-2 py-2 border-bottom" data-testid="waypoint-vias-toggle-row">
          <div className="col-12">
            <Button
              variant="link"
              size="sm"
              className="p-0 small text-decoration-none"
              data-testid="waypoint-vias-toggle"
              aria-expanded={!collapsed}
              aria-controls={viasId}
              onClick={() => setExpanded((open) => !open)}
            >
              <i
                className={`fa-solid ${collapsed ? 'fa-chevron-down' : 'fa-chevron-up'} me-1`}
                aria-hidden="true"
              ></i>
              {collapsed
                ? `Show ${vias.length} waypoint${vias.length === 1 ? '' : 's'}` +
                  (stopovers ? `, ${stopovers} stopover${stopovers === 1 ? '' : 's'}` : '')
                : 'Hide waypoints'}
            </Button>
          </div>
        </div>
      )}

      {/* The container renders whether or not the rows do, so aria-controls
          above never points at an element that isn't there. */}
      <div id={viasId}>
        {!collapsed &&
          vias.map((via, index) => renderWaypoint(via, `Waypoint ${index + 1}`, VIA_PLACEHOLDER))}
      </div>

      {renderAnchor(ANCHORS[1])}

      <div className="small text-body-secondary mt-2">
        {waypoints.length === 0 ? (
          <>
            Name where this trip starts and ends, then use “Lookup place” to
            find each one — or “Place on map” to tap it onto the chart yourself.
          </>
        ) : (
          <>
            New waypoints are named with their position — rename one to
            something like “Dodd Narrows”, or drag its marker on the map to
            move it. Renaming a point that’s already named drops it off the
            map; look the new name up, or place it on the map by hand.{' '}
            Mark one as a <strong>stopover</strong> for somewhere you stop
            without staying the night — a picnic stop, a fuel stop. It changes
            no dates.
          </>
        )}
      </div>
    </div>
  );
};

export default TripWaypoints;
